/**
 * Auth Telemetry - Lightweight monitoring for authentication health
 * 
 * Tracks auth events to help identify patterns and issues
 */

import { authLogger } from '@/core/utils/logger';

interface AuthEvent {
  type: 'login' | 'logout' | 'refresh' | 'error' | 'session_expired';
  success: boolean;
  timestamp: number;
  metadata?: Record<string, any>;
}

class AuthTelemetry {
  private static instance: AuthTelemetry;
  private events: AuthEvent[] = [];
  private readonly MAX_EVENTS = 100; // Keep last 100 events in memory
  
  private metrics = {
    loginAttempts: 0,
    loginSuccesses: 0,
    refreshAttempts: 0,
    refreshSuccesses: 0,
    errors: 0,
    sessionExpirations: 0,
    lastRefresh: null as number | null,
    lastError: null as { timestamp: number; type: string } | null,
  };
  
  private constructor() {}
  
  static getInstance(): AuthTelemetry {
    if (!AuthTelemetry.instance) {
      AuthTelemetry.instance = new AuthTelemetry();
    }
    return AuthTelemetry.instance;
  }
  
  /**
   * Track an auth event
   */
  trackEvent(type: AuthEvent['type'], success: boolean, metadata?: Record<string, any>): void {
    const event: AuthEvent = {
      type,
      success,
      timestamp: Date.now(),
      metadata,
    };
    
    // Add to events list (circular buffer)
    this.events.push(event);
    if (this.events.length > this.MAX_EVENTS) {
      this.events.shift();
    }
    
    // Update metrics
    switch (type) {
      case 'login':
        this.metrics.loginAttempts++;
        if (success) this.metrics.loginSuccesses++;
        break;
        
      case 'refresh':
        this.metrics.refreshAttempts++;
        if (success) {
          this.metrics.refreshSuccesses++;
          this.metrics.lastRefresh = Date.now();
        }
        break;
        
      case 'error':
        this.metrics.errors++;
        this.metrics.lastError = {
          timestamp: Date.now(),
          type: metadata?.errorType || 'unknown',
        };
        break;
        
      case 'session_expired':
        this.metrics.sessionExpirations++;
        break;
    }
    
    // Log significant events
    if (!success || type === 'error' || type === 'session_expired') {
      authLogger.warn(`[AuthTelemetry] ${type} event`, { success, metadata });
    }
  }
  
  /**
   * Get current metrics
   */
  getMetrics() {
    const successRate = this.metrics.loginAttempts > 0 
      ? (this.metrics.loginSuccesses / this.metrics.loginAttempts) * 100 
      : 100;
      
    const refreshSuccessRate = this.metrics.refreshAttempts > 0
      ? (this.metrics.refreshSuccesses / this.metrics.refreshAttempts) * 100
      : 100;
    
    return {
      ...this.metrics,
      successRate: successRate.toFixed(1) + '%',
      refreshSuccessRate: refreshSuccessRate.toFixed(1) + '%',
      minutesSinceLastRefresh: this.metrics.lastRefresh 
        ? Math.floor((Date.now() - this.metrics.lastRefresh) / 60000)
        : null,
    };
  }
  
  /**
   * Get recent events
   */
  getRecentEvents(count: number = 10): AuthEvent[] {
    return this.events.slice(-count);
  }
  
  /**
   * Check auth health
   */
  getHealthStatus(): 'healthy' | 'degraded' | 'unhealthy' {
    const metrics = this.getMetrics();
    
    // Unhealthy if recent errors
    if (this.metrics.lastError && 
        Date.now() - this.metrics.lastError.timestamp < 60000) { // Error in last minute
      return 'unhealthy';
    }
    
    // Unhealthy if many failures
    if (parseFloat(metrics.refreshSuccessRate) < 50) {
      return 'unhealthy';
    }
    
    // Degraded if some issues
    if (parseFloat(metrics.refreshSuccessRate) < 90 || 
        this.metrics.sessionExpirations > 2) {
      return 'degraded';
    }
    
    return 'healthy';
  }
  
  /**
   * Reset metrics (useful for testing)
   */
  reset(): void {
    this.events = [];
    this.metrics = {
      loginAttempts: 0,
      loginSuccesses: 0,
      refreshAttempts: 0,
      refreshSuccesses: 0,
      errors: 0,
      sessionExpirations: 0,
      lastRefresh: null,
      lastError: null,
    };
  }
}

// Export singleton instance
export const authTelemetry = AuthTelemetry.getInstance();

// Convenience tracking functions
export const trackLogin = (success: boolean, metadata?: Record<string, any>) => 
  authTelemetry.trackEvent('login', success, metadata);

export const trackRefresh = (success: boolean, metadata?: Record<string, any>) => 
  authTelemetry.trackEvent('refresh', success, metadata);

export const trackAuthError = (errorType: string, metadata?: Record<string, any>) => 
  authTelemetry.trackEvent('error', false, { errorType, ...metadata });

export const trackSessionExpired = (metadata?: Record<string, any>) => 
  authTelemetry.trackEvent('session_expired', false, metadata);